import React, { useState } from 'react';

import { Avatar, Center, Paper, Stack, Text } from '@mantine/core';
import { PoweredByLabel } from 'components/SmartAssistantComponents/PoweredByLabel';
import { AudioVisualizer } from 'components/TalkWithAgentButton/AudioVisualizer';
import { AnimatePresence } from 'framer-motion';
import { useSmartAssistantVisualConfigExtractor } from 'hooks/use-smart-assistant-theme-extractor';
import { GuestVoiceBasedSmartAssistantPageWrapper } from 'pages/GuestSmartAssistant/VoiceBasedSmartAssistant/GestVoiceBasedSmartAssistantPageWrapper';
import { GuestTalkWithAgentButton } from 'pages/GuestSmartAssistant/VoiceBasedSmartAssistant/GuestTalkWithAgentButton';
import { GuestSmartAssistantData } from 'shared/interfaces/SmartAssistant';
import { MBox } from 'shared/motion/MBox';

interface GuestVoiceBasedSmartAssistantPageProps {
  guestSmartAssistantData: GuestSmartAssistantData;
  avatarUrl?: string;
}

const AGENT_AVATAR_SIZE = 80;

export function GuestVoiceBasedSmartAssistantPage({
  guestSmartAssistantData,
  avatarUrl
}: GuestVoiceBasedSmartAssistantPageProps) {
  const [currentCombinedVolume, setCurrentCombinedVolume] = useState(0);
  const [isCallActive, setIsCallActive] = useState(false);

  const { smartAssistant } = guestSmartAssistantData;
  const smartAssistantVisualConfig = useSmartAssistantVisualConfigExtractor({
    smartAssistant,
    avatarUrl: avatarUrl
  });

  return (
    <GuestVoiceBasedSmartAssistantPageWrapper>
      <AnimatePresence mode="wait">
        <MBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}>
          <Paper withBorder shadow="sm" radius="lg" sx={{ padding: '30px 40px', minWidth: '300px' }}>
            <Stack align="center" spacing={20}>
              <MBox
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}>
                <Stack align="center" spacing="xs">
                  <Avatar src={avatarUrl} size={AGENT_AVATAR_SIZE} radius={AGENT_AVATAR_SIZE / 2} />
                  <Text size="xl" weight={500}>
                    {smartAssistant?.name}
                  </Text>
                </Stack>
              </MBox>

              <MBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.4 }}>
                <Center h={24} w="100%">
                  <AudioVisualizer volume={currentCombinedVolume} active={isCallActive} />
                </Center>
              </MBox>

              <MBox
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
                sx={{ width: '100%', minHeight: '36px', marginTop: 'auto' }}>
                <GuestTalkWithAgentButton
                  smartAssistantId={smartAssistant._id}
                  setCurrentCombinedVolume={setCurrentCombinedVolume}
                  setIsCallActive={setIsCallActive}
                />
              </MBox>
            </Stack>
            {!guestSmartAssistantData.removePoweredBy && (
              <MBox
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}>
                <PoweredByLabel
                  w="100%"
                  direction="column"
                  align="center"
                  pt={15}
                  mb={-15}
                  smartAssistantVisualConfig={smartAssistantVisualConfig}
                />
              </MBox>
            )}
          </Paper>
        </MBox>
      </AnimatePresence>
    </GuestVoiceBasedSmartAssistantPageWrapper>
  );
}
